<template>
  <b-form-group>
    <b-form-row>
      <b-col>
        <date-range-picker :label="$t('filter.period1')" :label-period-prefix="$t('filter.period1_prefix')"
          :date-picker-format="datePickerFormat" :date-filter-format="dateFilterFormat" :is-compare-report="true"
          :date-range-to-force="dateRangeToForce1" :use-stored-date-range="useStoredDateRange"
          :time-range-value="timeRangeValue"
          @dateRangeFilterUpdated="dateRangeFilter1Updated($event)"
          @labelPeriodHoursUpdated="labelPeriod1Updated($event)" />
      </b-col>
      <b-col>
        <date-range-picker :label="$t('filter.period2')" :label-period-prefix="$t('filter.period2_prefix')"
          :date-picker-format="datePickerFormat" :date-filter-format="dateFilterFormat" :is-compare-report="true"
          :date-range-to-force="dateRangeToForce2" :use-stored-date-range="useStoredDateRange"
          :time-range-value="timeRangeValue"
          @dateRangeFilterUpdated="dateRangeFilter2Updated($event)"
          @labelPeriodHoursUpdated="labelPeriod2Updated($event)" />
      </b-col>
    </b-form-row>
  </b-form-group>
</template>

<script>
import moment from "moment";
import Tools from "@/utils/tools";
import { dateFormatEnum } from "@/filter/dateFormat";
import DateRangePicker from "@/components/picker/DateRangePicker.vue";

export default {
  name: "DateRangeDualPicker",
  components: {
    DateRangePicker,
  },
  props: {
    /**
     * Heures de début et de fin provenant du filtre TimeRangePicker,
     * à transmettre aux date pickers pour recalculer les labels de période
     */
    timeRangeValue: {
      type: Object,
      default() {
        return null;
      },
    },
  },
  data() {
    return {
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      dateFilterFormat: dateFormatEnum.SQL_DATE_FORMAT,
      /**
       * Objet contenant toutes les données venant du dateRangePicker pour la période 1 :
       * {
       *    dateRangeFilter: filtre sur la période tel qu'attendu par cubeJs
       *    dateRangeStr: libellé de la période telle qu'affichée dans le picker
       *    labelPeriod: libellé complet de la période tel qu'affiché sous le picker
       * }
       */
      dateRangePickerData1: null,
      dateRangeFilterData2: null,
      dateRangeToForce1: [],
      dateRangeToForce2: [],
      useStoredDateRange: false,
    };
  },
  computed: {
    period1StartDateMoment() {
      if (this.period1IsValid) {
        return this.filterStringToMoment(
          this.dateRangePickerData1.dateRangeFilter.dateRange[0]
        );
      } else return null;
    },
    period1EndDateMoment() {
      if (this.period1IsValid) {
        return this.filterStringToMoment(
          this.dateRangePickerData1.dateRangeFilter.dateRange[1]
        );
      } else return null;
    },
    period1Length() {
      if (this.period1IsValid) {
        return this.period1EndDateMoment.diff(
          this.period1StartDateMoment,
          "days"
        );
      } else return 0;
    },
    period2Length() {
      if (this.period2IsValid) {
        const period2Start = this.filterStringToMoment(
          this.dateRangeFilterData2.dateRangeFilter.dateRange[0]
        );
        const period2End = this.filterStringToMoment(
          this.dateRangeFilterData2.dateRangeFilter.dateRange[1]
        );
        return period2End.diff(period2Start, "days");
      } else return 0;
    },
    period1IsValid() {
      return (
        this.dateRangePickerData1 &&
        this.dateRangePickerData1.dateRangeFilter &&
        this.dateRangePickerData1.dateRangeFilter.dateRange &&
        this.dateRangePickerData1.dateRangeFilter.dateRange.length === 2
      );
    },
    period2IsValid() {
      return (
        this.dateRangeFilterData2 &&
        this.dateRangeFilterData2.dateRangeFilter &&
        this.dateRangeFilterData2.dateRangeFilter.dateRange &&
        this.dateRangeFilterData2.dateRangeFilter.dateRange.length === 2
      );
    },
  },
  mounted() {
    this.setDefaultRanges();
  },
  methods: {
    setDefaultRanges() {
      const period1End = Tools.BusinessDateHelper.getTodaysBusinessDate();
      const period1Start = moment(period1End).subtract(6, "days");
      // NB : pour faire simple en attendant les retours client, la deuxième période sera settée automatiquement
      // de la même façon qu'à chaque changement de la période 1 par l'utilisateur
      // Si besoin d'une règle différente entre l'initialisation et les changements utilisateurs, il faudrait trouver comment éviter le conflit entre les évènements...
      this.dateRangeToForce1 = [
        this.momentToString(period1Start),
        this.momentToString(period1End),
      ];
    },
    dateRangeFilter1Updated(value) {
      this.dateRangePickerData1 = value;
      if (this.period1IsValid) {
        // Modifier en conséquence la deuxième période : même durée, 1 ans avant la période 1
        const period2Start = moment(this.period1StartDateMoment).subtract(
          1,
          "years"
        );
        const period2End = moment(period2Start).add(this.period1Length, "days");

        this.dateRangeToForce2 = [
          this.momentToString(period2Start),
          this.momentToString(period2End),
        ];
        // Pas besoin d'emettre l'évènement, ce sera fait à la modif de la 2ème période
      } else {
        this.emitDateRangeDualFiltersUpdated();
      }
    },
    dateRangeFilter2Updated(value) {
      this.dateRangeFilterData2 = value;
      this.emitDateRangeDualFiltersUpdated();
    },
    labelPeriod1Updated(value) {
      if (this.dateRangePickerData1) {
        this.dateRangePickerData1.labelPeriod = value.labelPeriod;
        this.emitLabelsUpdated();
      }
    },
    labelPeriod2Updated(value) {
      if (this.dateRangeFilterData2) {
        this.dateRangeFilterData2.labelPeriod = value.labelPeriod;
        this.emitLabelsUpdated();
      }
    },
    emitLabelsUpdated() {
      if (this.dateRangePickerData1 && this.dateRangeFilterData2) {
        this.$emit("dateRangeDualFiltersUpdated", {
          dateRangePickerData1: this.dateRangePickerData1,
          dateRangeFilterData2: this.dateRangeFilterData2,
        });
      }
    },
    emitDateRangeDualFiltersUpdated() {
      if (this.dateRangePickerData1 && this.dateRangeFilterData2) {
        if (
          this.period1IsValid &&
          this.period2IsValid &&
          this.period1Length !== this.period2Length
        ) {
          const message = Tools.StringHelper.format(
            this.$t("alerts.alertUnevenPeriodsMessageTemplate"),
            this.period1Length + 1,
            this.period2Length + 1
          );
          Tools.Alerts.Alert(
            this.$swal,
            message,
            this.$t("alerts.alertUnevenPeriodsTitle")
          );
          // TODO trouver comment empêcher le picker de se réouvrir à la fermeture de l'alerte...
        }
        this.$emit("dateRangeDualFiltersUpdated", {
          dateRangePickerData1: this.dateRangePickerData1,
          dateRangeFilterData2: this.dateRangeFilterData2,
        });
      }
    },
    momentToString(momentDate) {
      return momentDate.format(this.datePickerFormat).toString();
    },
    filterStringToMoment(filterStringDate) {
      return moment(filterStringDate, this.dateFilterFormat);
    },
  },
};
</script>
